import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";

// GraphQL queries
const CLIENT_LIST_QUERY = gql`
  query ClientList($filter: ClientFilter, $params: PaginationAndSorting) {
    clientList(filter: $filter, params: $params) {
      code
      data {
        id
        clientType
        companyName
        nameTitle
        firstName
        lastName
        email
        phone
        website
        notes
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CLIENT_GET_QUERY = gql`
  query ClientGet($id: ID) {
    clientGet(id: $id) {
      code
      data {
        id
        clientType
        companyName
        nameTitle
        firstName
        lastName
        email
        phone
        website
        notes
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CLIENT_GET_MANY_QUERY = gql`
  query ClientGetMany($ids: [ID]) {
    clientGetMany(ids: $ids) {
      code
      data {
        id
        clientType
        companyName
        nameTitle
        firstName
        lastName
        email
        phone
        website
        notes
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const CLIENT_CREATE_MUTATION = gql`
  mutation ClientCreate($params: ClientInput) {
    clientCreate(params: $params) {
      code
      data {
        id
        clientType
        companyName
        nameTitle
        firstName
        lastName
        email
        phone
        website
        notes
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CLIENT_UPDATE_MUTATION = gql`
  mutation ClientUpdate($where: ClientWhereInput, $params: ClientInput) {
    clientUpdate(where: $where, params: $params) {
      code
      data {
        id
        clientType
        companyName
        nameTitle
        firstName
        lastName
        email
        phone
        website
        notes
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CLIENT_REMOVE_MUTATION = gql`
  mutation ClientRemove($where: ClientWhereInput) {
    clientRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface Client {
  id: string;
  clientType?: string;
  companyName?: string;
  nameTitle?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  website?: string;
  notes?: string;
  status?: number;
}

export interface ClientFilter {
  id?: string[] | null | undefined;
  alongWithId?: string[] | null | undefined;
  companyName?: string | null | undefined;
  searchKeyword?: string | null | undefined;
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  email?: string | null | undefined;
  phone?: string | null | undefined;
  status?: number[] | null | undefined;
}

export interface ClientInput {
  clientType?: string | null | undefined;
  companyName?: string | null | undefined;
  nameTitle?: string | null | undefined;
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  email?: string | null | undefined;
  phone?: string | null | undefined;
  website?: string | null | undefined;
  notes?: string | null | undefined;
  status?: number | null | undefined;
}

export interface ClientWhereInput {
  id: string;
}

/**
 * Client service that handles all client-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove
 */
export class ClientService {
  /**
   * Get a list of clients with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: ClientFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(CLIENT_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single client by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(CLIENT_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple clients by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(CLIENT_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new client
   */
  static async create(params: ClientInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(CLIENT_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing client
   */
  static async update(id: string, params: ClientInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(CLIENT_UPDATE_MUTATION, {
      where: { id },
      params,
    });
    return result;
  }

  /**
   * Remove/delete a client
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(CLIENT_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }
}
